import { SupportedEntitlements } from '@core/models/entitlements';
import { WorkspaceDTO } from '@core/models/workspace';
import { datadogLogs } from '@datadog/browser-logs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { BaseApi } from '../../../core/api';
import { getInitials } from '../../../core/models';
import type {
  LicensingStatus,
  Organization,
  OrganizationDTO,
  VerifyDTO,
} from '../../../core/models/organization';
import type { EntitlementKey } from '../../../redux/organizations/models';
import { getUnitSystem } from '../../../utils/unitUtils';

dayjs.extend(isBetween);

export const getOrganizationWorkspaceDetails = async (workspace_id: string) => {
  const { data } = await BaseApi.get<WorkspaceDTO>(`/workspaces/${workspace_id}`);
  return data;
};

export const getOrganizationsWithPermissions = async (
  organizations: OrganizationDTO[]
): Promise<Organization[]> => {
  const operations = organizations.map((organization) => ({
    action: 'organization:write',
    resource: organization.crn,
  }));

  try {
    const { data } = await BaseApi.post<VerifyDTO>('/auth/verify', { operations });

    const allowedOperations = data.allowed_operations;

    return organizations.map<Organization>((org) => ({
      ...org,
      unitSystem: getUnitSystem(org.unit_system),
      hideFeatures: false,
      is_write_allowed: allowedOperations.some((operation) => operation.resource === org.crn),
      entitlements: new Map<string, string>(),
      entitlementEndDate: new Date().toISOString(),
      licensed: false,
      initials: getInitials(org.name),
      permissions: new Map(),
    }));
  } catch (error) {
    datadogLogs.logger.error('getOrganizationsWithPermissions: failed to verify permissions', {
      error: error instanceof Error ? error.message : String(error),
    });

    return organizations.map<Organization>((org) => ({
      ...org,
      unitSystem: getUnitSystem(org.unit_system),
      hideFeatures: false,
      is_write_allowed: false,
      entitlements: new Map<string, string>(),
      entitlementEndDate: new Date().toISOString(),
      licensed: false,
      initials: getInitials(org.name),
      permissions: new Map(),
    }));
  }
};

export const getOrganizationsWithPlans = async (organizations: Organization[]) => {
  const orgs = await Promise.allSettled(
    organizations.map(
      async (org): Promise<Organization | null> => {
        try {
          const licenseResponse = await BaseApi.get<LicensingStatus>('/licensing/status', {
            params: {
              org: org.id,
            },
          });

          const organizationApps = licenseResponse.data.apps;
          const activePlans = organizationApps.filter((app) =>
            dayjs().isBetween(app.contract.start, app.contract.end, null, '[]')
          );

          // Keep the most recent active plan (latest contract end) per app id
          const mostRecentPlanByApp = activePlans.reduce<
            Map<string, LicensingStatus['apps'][number]>
          >((acc, app) => {
            const existing = acc.get(app.id);
            if (!existing || dayjs(app.contract.end).isAfter(existing.contract.end)) {
              acc.set(app.id, app);
            }
            return acc;
          }, new Map());

          const currentPlan = mostRecentPlanByApp.get(import.meta.env.VITE_APP_IMAGERY_APPID);

          if (currentPlan) {
            // Set organization as licensed
            const entitlementKeys: EntitlementKey[] = currentPlan.plan?.entitlements || [];

            // Aggregate the entitlements of the most recent active plan per app
            const mappedEntitlementKeys = new Map<string, string>();
            for (const { plan } of mostRecentPlanByApp.values()) {
              (plan?.entitlements ?? []).forEach((key) => {
                mappedEntitlementKeys.set(key.key, key.name);
              });
            }

            const entitlements = addAppEntitlements(mappedEntitlementKeys, organizationApps);

            return {
              ...org,
              entitlements,
              hideFeatures: entitlementKeys.some((item) => item.key === 'hidden'),
              plan: currentPlan?.plan?.name,
              planId: currentPlan?.plan?.id,
              planExpiration: new Date(currentPlan?.contract?.end),
              licensed: true,
            };
          } else {
            // No active imagery plan, so reflect the most recently expired one
            const mostRecentExpiration = organizationApps
              .filter((app) => app.id === import.meta.env.VITE_APP_IMAGERY_APPID)
              .map((app) => app.contract.end)
              .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0];

            // Set organization as not licensed
            return {
              ...org,
              entitlements: new Map<string, string>(),
              planExpiration: mostRecentExpiration
                ? new Date(mostRecentExpiration)
                : dayjs().subtract(1, 'day').toDate(),
              licensed: false,
            };
          }
        } catch (error) {
          datadogLogs.logger.error('getOrganizationsWithPlans: failed to load org licensing', {
            error: error instanceof Error ? error.message : String(error),
            org: org.id,
          });

          return null;
        }
      }
    )
  );

  return orgs.reduce<Organization[]>((acc, current) => {
    if (current.status === 'fulfilled' && current.value) {
      acc.push(current.value);
    }
    return acc;
  }, []);
};

export const getOrganizationCropCyclePermission = async (organization: Organization) => {
  try {
    const operation = {
      action: 'crop_cycle:write',
      resource: organization.crn,
    };

    const { data } = await BaseApi.post<VerifyDTO>('/auth/verify', { operations: [operation] });

    return data.allowed_operations.some((operation) => operation.resource === organization.crn);
  } catch (error) {
    datadogLogs.logger.error('getOrganizationCropCyclePermission: failed to verify permission', {
      error: error instanceof Error ? error.message : String(error),
      org: organization.id,
    });

    return false;
  }
};

const APP_PLAN_ENTITLEMENTS = new Map<string, readonly SupportedEntitlements[]>([
  [import.meta.env.VITE_APP_TRIALING_APPID, [SupportedEntitlements.TRIALING_PLOTS]],
  [import.meta.env.VITE_APP_PROTECTOR_APPID, [SupportedEntitlements.PROTECTOR_MY_FILES]],
  [import.meta.env.VITE_APP_PLANTING_APPID, [SupportedEntitlements.VALIDATION_BLOCKS]],
  // NEMA has no entitlement of its own — an active AppPlan contract grants access
  [import.meta.env.VITE_APP_NEMA_APPID, [SupportedEntitlements.NEMA_DIGITAL]],
]);

export function addAppEntitlements(
  currentEntitlements: Map<string, string>,
  organizationApps: LicensingStatus['apps']
) {
  const newMap = new Map(currentEntitlements);

  return organizationApps.reduce<Map<string, string>>((acc, current) => {
    const appEntitlements = APP_PLAN_ENTITLEMENTS.get(current.id);

    if (
      appEntitlements &&
      dayjs().isBetween(current.contract.start, current.contract.end, null, '[]')
    ) {
      appEntitlements.forEach((item) => {
        acc.set(item, item);
      });
    }

    return acc;
  }, newMap);
}
